/**
 * The company's day (D-119): days and months turn at midnight in the company's time zone
 * (`tenants.default_time_zone`, Europe/Istanbul unless the company chose another). Daily budgets reset then,
 * "Bugün" and "Bu ay" count from then, and "N gün önce" counts the company's days, wherever the reader is.
 */
export const DEFAULT_COMPANY_TIME_ZONE = 'Europe/Istanbul';

const formatters = new Map<string, Intl.DateTimeFormat>();

function formatter(timeZone: string): Intl.DateTimeFormat {
  let found = formatters.get(timeZone);
  if (!found) {
    found = new Intl.DateTimeFormat('en-US', {
      timeZone,
      hourCycle: 'h23',
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      second: 'numeric',
    });
    formatters.set(timeZone, found);
  }
  return found;
}

export interface ZonedParts {
  year: number;
  /** 1–12 */
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
}

/** What a clock on the wall in the zone shows at an instant. */
export function zonedParts(instant: Date, timeZone: string): ZonedParts {
  const parts: Record<string, number> = {};
  for (const part of formatter(timeZone).formatToParts(instant)) if (part.type !== 'literal') parts[part.type] = Number(part.value);
  return {
    year: parts.year ?? 1970,
    month: parts.month ?? 1,
    day: parts.day ?? 1,
    hour: (parts.hour ?? 0) % 24,
    minute: parts.minute ?? 0,
    second: parts.second ?? 0,
  };
}

/** How far the zone's clock is ahead of UTC at an instant, in milliseconds. */
function offsetAt(instant: number, timeZone: string): number {
  const p = zonedParts(new Date(instant), timeZone);
  return Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second) - Math.floor(instant / 1000) * 1000;
}

/** The first instant of a calendar day in the zone (its 00:00). */
export function zonedMidnight(year: number, month: number, day: number, timeZone: string): Date {
  const wall = Date.UTC(year, month - 1, day);
  const first = wall - offsetAt(wall, timeZone);
  // the offset at the answer differs when a clock change lies between: once more with that one
  const second = wall - offsetAt(first, timeZone);
  return new Date(second);
}

/** The calendar day after (or before) a date, as numbers: 31 October + 1 is 1 November. */
function shiftDays(p: { year: number; month: number; day: number }, days: number) {
  const date = new Date(Date.UTC(p.year, p.month - 1, p.day + days));
  return { year: date.getUTCFullYear(), month: date.getUTCMonth() + 1, day: date.getUTCDate() };
}

export function startOfZonedDay(instant: Date, timeZone: string): Date {
  const p = zonedParts(instant, timeZone);
  return zonedMidnight(p.year, p.month, p.day, timeZone);
}

/** The next 00:00 in the zone: when a daily budget resets. */
export function nextZonedDay(instant: Date, timeZone: string): Date {
  const next = shiftDays(zonedParts(instant, timeZone), 1);
  return zonedMidnight(next.year, next.month, next.day, timeZone);
}

export function startOfZonedMonth(instant: Date, timeZone: string): Date {
  const p = zonedParts(instant, timeZone);
  return zonedMidnight(p.year, p.month, 1, timeZone);
}

/** The first 00:00 of the next month in the zone: when a monthly budget resets. */
export function nextZonedMonth(instant: Date, timeZone: string): Date {
  const p = zonedParts(instant, timeZone);
  return p.month === 12 ? zonedMidnight(p.year + 1, 1, 1, timeZone) : zonedMidnight(p.year, p.month + 1, 1, timeZone);
}

/** The zone's date, YYYY-MM-DD: the key of a day's counters. */
export function zonedDateKey(instant: Date, timeZone: string): string {
  const p = zonedParts(instant, timeZone);
  return `${p.year}-${String(p.month).padStart(2, '0')}-${String(p.day).padStart(2, '0')}`;
}

/** Whole days between two instants by the zone's calendar: 0 on the same day, 1 for the day before, … */
export function zonedDaysBetween(earlier: Date, later: Date, timeZone: string): number {
  const a = zonedParts(earlier, timeZone);
  const b = zonedParts(later, timeZone);
  return Math.round((Date.UTC(b.year, b.month - 1, b.day) - Date.UTC(a.year, a.month - 1, a.day)) / 86_400_000);
}

/** The day of the week in the zone: 0 Sunday … 6 Saturday. */
export function zonedWeekday(instant: Date, timeZone: string): number {
  const p = zonedParts(instant, timeZone);
  return new Date(Date.UTC(p.year, p.month - 1, p.day)).getUTCDay();
}
